import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Chip,
  FormControlLabel,
  Link,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useApiGet } from '../../utils/httpClientTs';
import {
  getElfPayPalActivity,
  getElfPayPalTransactions,
} from '../../endpoints/financeElfPayments';
import ServerSideTable, {
  ServerSideTableColumn,
} from '../../Components/ServerSideTable';
import type {
  PayPalActivityRow,
  PayPalTransactionRow,
} from '../../types/financePayments';

const fmtMoney = (n: number) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

const formatApplyStatus = (status?: string) => {
  const s = (status || '').trim().toLowerCase();
  if (s === 'linked') return 'Linked';
  if (s === 'applied') return 'Applied';
  return '—';
};

type Feed = 'settlements' | 'activity';
type RecurringFilter = 'all' | 'recurring' | 'one_time';

const ElfPaymentsPayPalPage: React.FC = () => {
  const [feed, setFeed] = useState<Feed>('settlements');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [unmatchedOnly, setUnmatchedOnly] = useState(false);
  const [hasErrorOnly, setHasErrorOnly] = useState(false);
  const [recurringFilter, setRecurringFilter] =
    useState<RecurringFilter>('all');

  const recurringParam =
    recurringFilter === 'recurring'
      ? true
      : recurringFilter === 'one_time'
        ? false
        : null;

  const settlementsQuery = useApiGet(
    [
      'finance-elf-payments-paypal-settlements',
      page,
      rowsPerPage,
      unmatchedOnly,
      hasErrorOnly,
      recurringFilter,
    ],
    () =>
      getElfPayPalTransactions({
        page: page + 1,
        page_size: rowsPerPage,
        unmatched: unmatchedOnly,
        has_error: hasErrorOnly,
        recurring: recurringParam,
      }),
    { enabled: feed === 'settlements', retry: 1 }
  );

  const activityQuery = useApiGet(
    [
      'finance-elf-payments-paypal-activity',
      page,
      rowsPerPage,
      hasErrorOnly,
      recurringFilter,
    ],
    () =>
      getElfPayPalActivity({
        page: page + 1,
        page_size: rowsPerPage,
        has_error: hasErrorOnly,
        recurring: recurringParam,
      }),
    { enabled: feed === 'activity', retry: 1 }
  );

  const isLoading =
    feed === 'settlements'
      ? settlementsQuery.isLoading
      : activityQuery.isLoading;
  const pagination =
    feed === 'settlements'
      ? settlementsQuery.data?.pagination
      : activityQuery.data?.pagination;

  const settlementColumns: ServerSideTableColumn<PayPalTransactionRow>[] =
    useMemo(
      () => [
        {
          id: 'paypal_transaction_id',
          label: 'Transaction',
          width: '14%',
          firstCell: true,
          sortable: false,
          render: (_v, row) => (
            <Link
              component={RouterLink}
              to={`/finance/elf-payments/paypal/${row.id}`}
              variant="body2"
              sx={{ wordBreak: 'break-all' }}
            >
              {row.paypal_transaction_id}
            </Link>
          ),
        },
        {
          id: 'matched',
          label: 'Match',
          width: '9%',
          sortable: false,
          render: (_v, row) =>
            row.matched ? (
              <Chip
                size="small"
                label="Matched"
                color="success"
                variant="outlined"
              />
            ) : (
              <Chip size="small" label="Unmatched" color="warning" />
            ),
        },
        {
          id: 'apply_status',
          label: 'Apply',
          width: '9%',
          sortable: false,
          render: (_v, row) => formatApplyStatus(row.apply_status),
        },
        {
          id: 'has_error',
          label: 'Issues',
          width: '9%',
          sortable: false,
          render: (_v, row) =>
            row.has_error ? (
              <Chip size="small" label="Error" color="error" />
            ) : (
              '—'
            ),
        },
        {
          id: 'is_recurring',
          label: 'Schedule',
          width: '9%',
          sortable: false,
          render: (_v, row) => (row.is_recurring ? 'Recurring' : 'One-time'),
        },
        {
          id: 'amount',
          label: 'Amount',
          width: '9%',
          sortable: false,
          render: (_v, row) =>
            `${fmtMoney(row.amount)} ${row.currency_code || ''}`.trim(),
        },
        {
          id: 'status',
          label: 'Status',
          width: '9%',
          sortable: false,
          render: (_v, row) => row.status || '—',
        },
        {
          id: 'response_msg',
          label: 'Response',
          width: '12%',
          sortable: false,
          render: (_v, row) => (
            <Typography variant="body2" sx={{ whiteSpace: 'normal' }}>
              {row.response_msg ||
                row.result_code_msg ||
                row.fund_status ||
                '—'}
            </Typography>
          ),
        },
        {
          id: 'payer_label',
          label: 'Member',
          width: '12%',
          sortable: false,
          render: (_v, row) => row.payer_label || '—',
        },
        {
          id: 'transaction_time',
          label: 'Time',
          width: '10%',
          sortable: false,
          render: (_v, row) =>
            row.transaction_time
              ? new Date(row.transaction_time).toLocaleString()
              : '—',
        },
      ],
      []
    );

  const activityColumns: ServerSideTableColumn<PayPalActivityRow>[] = useMemo(
    () => [
      {
        id: 'paypal_transaction_id',
        label: 'Transaction',
        width: '18%',
        firstCell: true,
        sortable: false,
        render: (_v, row) => (
          <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
            {row.paypal_transaction_id}
          </Typography>
        ),
      },
      {
        id: 'has_error',
        label: 'Issues',
        width: '10%',
        sortable: false,
        render: (_v, row) =>
          row.has_error ? (
            <Chip size="small" label="Error" color="error" />
          ) : (
            '—'
          ),
      },
      {
        id: 'is_recurring',
        label: 'Schedule',
        width: '10%',
        sortable: false,
        render: (_v, row) => (row.is_recurring ? 'Recurring' : 'One-time'),
      },
      {
        id: 'amount',
        label: 'Amount',
        width: '12%',
        sortable: false,
        render: (_v, row) =>
          `${fmtMoney(row.amount)} ${row.currency_code || ''}`.trim(),
      },
      {
        id: 'status',
        label: 'Status',
        width: '12%',
        sortable: false,
        render: (_v, row) => row.status || '—',
      },
      {
        id: 'response_msg',
        label: 'Response',
        width: '18%',
        sortable: false,
        render: (_v, row) => (
          <Typography variant="body2" sx={{ whiteSpace: 'normal' }}>
            {row.response_msg ||
              row.result_code_msg ||
              (row.result_code ? `Code ${row.result_code}` : '—')}
          </Typography>
        ),
      },
      {
        id: 'payer_label',
        label: 'Member',
        width: '14%',
        sortable: false,
        render: (_v, row) => row.payer_label || '—',
      },
      {
        id: 'transaction_time',
        label: 'Time',
        width: '14%',
        sortable: false,
        render: (_v, row) =>
          row.transaction_time
            ? new Date(row.transaction_time).toLocaleString()
            : '—',
      },
    ],
    []
  );

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {feed === 'settlements'
          ? 'Settlements: match review and settled payment rows. Use Activity for declines / insufficient funds.'
          : 'Activity: daily Payflow attempts including declines and NSF. Not the same as settlement deposits.'}
      </Typography>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ sm: 'center' }}
        sx={{ mb: 2 }}
        flexWrap="wrap"
      >
        <ToggleButtonGroup
          exclusive
          size="small"
          value={feed}
          onChange={(_, v: Feed | null) => {
            if (!v) return;
            setFeed(v);
            setPage(0);
            if (v === 'activity') setUnmatchedOnly(false);
          }}
        >
          <ToggleButton value="settlements">Settlements</ToggleButton>
          <ToggleButton value="activity">Activity</ToggleButton>
        </ToggleButtonGroup>

        {feed === 'settlements' && (
          <FormControlLabel
            control={
              <Switch
                checked={unmatchedOnly}
                onChange={(e) => {
                  setUnmatchedOnly(e.target.checked);
                  setPage(0);
                }}
              />
            }
            label="Unmatched only"
          />
        )}
        <FormControlLabel
          control={
            <Switch
              checked={hasErrorOnly}
              onChange={(e) => {
                setHasErrorOnly(e.target.checked);
                setPage(0);
              }}
            />
          }
          label="Errors only"
        />
        <ToggleButtonGroup
          exclusive
          size="small"
          value={recurringFilter}
          onChange={(_, v: RecurringFilter | null) => {
            if (!v) return;
            setRecurringFilter(v);
            setPage(0);
          }}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="recurring">Recurring</ToggleButton>
          <ToggleButton value="one_time">One-time</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {feed === 'settlements' ? (
        <ServerSideTable<PayPalTransactionRow>
          data={settlementsQuery.data?.data ?? []}
          columns={settlementColumns}
          loading={isLoading}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={pagination?.total_count ?? 0}
          totalPages={pagination?.total_pages ?? 0}
          hasNext={pagination?.has_next ?? false}
          hasPrev={pagination?.has_prev ?? false}
          order="desc"
          orderBy="transaction_time"
          onPageChange={setPage}
          onRowsPerPageChange={(n) => {
            setRowsPerPage(n);
            setPage(0);
          }}
          onSort={() => {}}
          emptyMessage="No PayPal settlement transactions found."
        />
      ) : (
        <ServerSideTable<PayPalActivityRow>
          data={activityQuery.data?.data ?? []}
          columns={activityColumns}
          loading={isLoading}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={pagination?.total_count ?? 0}
          totalPages={pagination?.total_pages ?? 0}
          hasNext={pagination?.has_next ?? false}
          hasPrev={pagination?.has_prev ?? false}
          order="desc"
          orderBy="transaction_time"
          onPageChange={setPage}
          onRowsPerPageChange={(n) => {
            setRowsPerPage(n);
            setPage(0);
          }}
          onSort={() => {}}
          emptyMessage="No PayPal activity transactions found."
        />
      )}
    </Box>
  );
};

export default ElfPaymentsPayPalPage;
